import { computed, nextTick, onScopeDispose, onMounted, ref, toValue, unref, watch } from 'vue';
import { useQuery, useQueryClient } from '@tanstack/vue-query';

import { useEventHub } from '@satellite/composables/useEventHub';
import { DEFAULT_INVALIDATE_OPTIONS, queryKeys } from './queryKeys';
import assetVisitService from '@satellite/services/asset-visit-service';
import {
  EntityPresenterV2Mode,
  isEntityPresenterV2QueryEnabled,
  isLazyLiveMode,
  isStaticMode,
  subscribesToEventHub
} from './presenterV2EntityMode';

import type { ComputedRef, Ref } from 'vue';
import type { Maybe } from '@nova/core';

import type { PresenterPlaceholderData } from './presenterV2Placeholder';
import type { YardAssetDetails } from './yardAssetPresenterV2Shared';

const ASSET_CONTAINER_QUERY_JOINS = [
  'assetContainerEvents||id,createDateTime,eventType,assetVisit',
  'assetContainerEvents.assetVisit||id,visitType,companyId,companyHint',
  'assetVisit||id,visitType,companyId',
  'dropAppointment||id,userId',
  'pickupAppointment||id,userId'
];

const ASSET_CONTAINER_EVENT_HUB_TOPICS = [
  'update-AssetContainer',
  'create-AssetContainerEvent',
  'update-AssetContainerEvent',
  'create-SpotAssignment',
  'update-SpotAssignment',
  'delete-SpotAssignment',
  'create-YardTask',
  'update-YardTask',
  'delete-YardTask'
] as const;

export interface UseYardAssetFetcherV2Options {
  /**
   * Asset (container) ID to fetch. Can be a ref for reactivity.
   * Optional if initialAssetData (with id) is provided.
   */
  assetId?: Ref<Maybe<string>> | ComputedRef<Maybe<string>>;

  /**
   * Optional initial asset data used as initialData for the query.
   */
  initialAssetData?:
    | Ref<Maybe<YardAssetDetails>>
    | ComputedRef<Maybe<YardAssetDetails>>
    | YardAssetDetails;

  /**
   * Optional incomplete asset data shown as placeholderData while the query fetches.
   */
  placeholderAssetData?:
    | Ref<Maybe<PresenterPlaceholderData<YardAssetDetails>>>
    | ComputedRef<Maybe<PresenterPlaceholderData<YardAssetDetails>>>
    | PresenterPlaceholderData<YardAssetDetails>;

  /**
   * When true, the query runs without viewport gating.
   * Default: false
   */
  headless?: boolean;

  /**
   * - `live`: fetches when enabled and subscribes to EventHub updates (default)
   * - `lazy-live`: no fetch until first matching EventHub event, refresh(), or invalidate()
   * - `static`: no fetch/subscriptions; returns staticAssetData only
   */
  mode?: EntityPresenterV2Mode;

  /**
   * Data source for `mode: 'static'`.
   */
  staticAssetData?:
    | Ref<Maybe<YardAssetDetails>>
    | ComputedRef<Maybe<YardAssetDetails>>
    | YardAssetDetails;

  staleTime?: number;

  onUpdateReceived?: (asset: YardAssetDetails) => void;
}

export function useYardAssetFetcherV2(options: UseYardAssetFetcherV2Options) {
  const {
    assetId,
    initialAssetData,
    placeholderAssetData,
    headless = false,
    mode = EntityPresenterV2Mode.Live,
    staticAssetData,
    staleTime = Infinity,
    onUpdateReceived
  } = options;

  const eventHub = useEventHub();
  const queryClient = useQueryClient();

  const effectiveAssetId = computed((): string | null => {
    const explicitId = unref(assetId);
    const fromObject = toValue(initialAssetData)?.id ?? null;

    if (explicitId) {
      if (fromObject && fromObject !== explicitId) {
        console.warn(
          '[useYardAssetFetcherV2] assetId mismatch:',
          `explicit assetId (${explicitId}) does not match asset.id (${fromObject}). Using explicit assetId.`
        );
      }
      return explicitId;
    }
    return fromObject;
  });

  const initialAsset = computed(() => toValue(initialAssetData) ?? undefined);
  const placeholderAsset = computed(() => toValue(placeholderAssetData) ?? undefined);
  const staticAsset = computed(() => toValue(staticAssetData) ?? undefined);

  if (
    isStaticMode(mode) &&
    (toValue(initialAssetData) != null || toValue(placeholderAssetData) != null)
  ) {
    console.warn(
      '[useYardAssetFetcherV2] mode=static ignores initialAssetData/placeholderAssetData. Use staticAssetData.'
    );
  }

  if (isLazyLiveMode(mode) && toValue(initialAssetData) != null) {
    console.warn(
      '[useYardAssetFetcherV2] mode=lazy-live does not use initialAssetData for the query cache; use placeholderAssetData for partial UI.'
    );
  }

  const lazyLiveUnlocked = ref(!isLazyLiveMode(mode));
  const isInViewport = ref(headless);
  const pendingInvalidation = ref(false);

  const wakeLazyLive = () => {
    if (isLazyLiveMode(mode) && !lazyLiveUnlocked.value) {
      lazyLiveUnlocked.value = true;
    }
  };

  const assetQuery = useQuery({
    queryKey: computed(() =>
      effectiveAssetId.value ? queryKeys.assetContainer(effectiveAssetId.value) : []
    ),
    queryFn: async ({ signal }) => {
      if (!effectiveAssetId.value) {
        return null;
      }

      const response = await assetVisitService.getAssetContainerByIdV2(effectiveAssetId.value, {
        requestConfig: {
          signal,
          params: { join: ASSET_CONTAINER_QUERY_JOINS }
        },
        enableCache: true
      });

      return response as YardAssetDetails;
    },
    enabled: computed(() =>
      isEntityPresenterV2QueryEnabled({
        mode,
        hasEffectiveId: Boolean(effectiveAssetId.value),
        isInViewport: isInViewport.value,
        lazyLiveUnlocked: lazyLiveUnlocked.value
      })
    ),
    initialData: computed(() =>
      isStaticMode(mode) || isLazyLiveMode(mode) ? undefined : initialAsset.value
    ),
    placeholderData: computed(() => {
      if (isStaticMode(mode)) {
        return undefined;
      }
      return placeholderAsset.value as YardAssetDetails | undefined;
    }),
    staleTime,
    refetchOnWindowFocus: false
  });

  const data = computed((): YardAssetDetails | null => {
    const assetData = isStaticMode(mode) ? staticAsset.value : assetQuery.data.value;

    if (!assetData) {
      if (isStaticMode(mode)) {
        console.warn('[useYardAssetFetcherV2] mode=static requires staticAssetData.');
      }
      return null;
    }

    return assetData;
  });

  const spotId = computed((): Maybe<string> => {
    return (
      data.value?.spotAssignments?.[0]?.spotId ??
      data.value?.assetVisit?.spotAssignments?.[0]?.spotId ??
      null
    );
  });

  const isLoading = computed(() => (isStaticMode(mode) ? false : assetQuery.isLoading.value));
  const isFetching = computed(() => (isStaticMode(mode) ? false : assetQuery.isFetching.value));
  const error = computed(() => (isStaticMode(mode) ? null : assetQuery.error.value));

  const invalidate = () => {
    if (isStaticMode(mode)) {
      return;
    }
    wakeLazyLive();
    if (effectiveAssetId.value) {
      queryClient.invalidateQueries(
        {
          queryKey: queryKeys.assetContainer(effectiveAssetId.value)
        },
        DEFAULT_INVALIDATE_OPTIONS
      );
    }
  };

  const refresh = async () => {
    if (isStaticMode(mode)) {
      return;
    }
    wakeLazyLive();
    await nextTick();
    await assetQuery.refetch();
  };

  const deferOrInvalidate = () => {
    wakeLazyLive();

    if (!isInViewport.value) {
      pendingInvalidation.value = true;
      return;
    }

    invalidate();
  };

  const handleIntersect = (entry?: { isIntersecting?: boolean } | boolean) => {
    if (headless) {
      return;
    }

    const isIntersecting = typeof entry === 'boolean' ? entry : (entry?.isIntersecting ?? true);
    isInViewport.value = isIntersecting;

    if (isIntersecting && pendingInvalidation.value) {
      pendingInvalidation.value = false;
      invalidate();
    }
  };

  const handleUpdateAssetContainer = (event: { id?: string }) => {
    if (event?.id !== effectiveAssetId.value) {
      return;
    }

    deferOrInvalidate();
  };

  const handleUpdateAssetContainerEvent = (event: {
    assetVisit?: { id?: string };
    assetContainerId?: string;
  }) => {
    const matchesContainer = event?.assetContainerId === effectiveAssetId.value;
    const matchesVisit =
      event?.assetVisit?.id != null && event.assetVisit.id === data.value?.assetVisit?.id;

    if (!matchesContainer && !matchesVisit) {
      return;
    }

    deferOrInvalidate();
  };

  const handleSpotAssignmentEvent = (event: { assetContainerId?: string }) => {
    if (event.assetContainerId !== effectiveAssetId.value) {
      return;
    }

    deferOrInvalidate();
  };

  const handleYardTaskEvent = (event: { type: string; child: { assetContainerId?: string } }) => {
    if (event?.type !== 'move' || event?.child?.assetContainerId !== effectiveAssetId.value) {
      return;
    }

    deferOrInvalidate();
  };

  const eventHubHandlers: Record<
    (typeof ASSET_CONTAINER_EVENT_HUB_TOPICS)[number],
    (...args: never[]) => void
  > = {
    'update-AssetContainer': handleUpdateAssetContainer,
    'create-AssetContainerEvent': handleUpdateAssetContainerEvent,
    'update-AssetContainerEvent': handleUpdateAssetContainerEvent,
    'create-SpotAssignment': handleSpotAssignmentEvent,
    'update-SpotAssignment': handleSpotAssignmentEvent,
    'delete-SpotAssignment': handleSpotAssignmentEvent,
    'create-YardTask': handleYardTaskEvent,
    'update-YardTask': handleYardTaskEvent,
    'delete-YardTask': handleYardTaskEvent
  };

  const subscribeEventHub = () => {
    if (!subscribesToEventHub(mode)) {
      return;
    }

    for (const [topic, handler] of Object.entries(eventHubHandlers)) {
      eventHub.on(topic, handler);
    }
  };

  const unsubscribeEventHub = () => {
    if (!subscribesToEventHub(mode)) {
      return;
    }

    for (const [topic, handler] of Object.entries(eventHubHandlers)) {
      eventHub.off(topic, handler);
    }
  };

  watch(data, newAsset => {
    if (onUpdateReceived && newAsset) {
      onUpdateReceived(newAsset);
    }
  });

  watch(effectiveAssetId, (newId, oldId) => {
    if (newId !== oldId) {
      pendingInvalidation.value = false;
      lazyLiveUnlocked.value = !isLazyLiveMode(mode);
    }
  });

  onMounted(subscribeEventHub);
  onScopeDispose(unsubscribeEventHub);

  return {
    data,
    spotId,
    isLoading,
    isFetching,
    error,
    refresh,
    invalidate,
    onIntersect: handleIntersect,
    query: assetQuery
  };
}
